import React, { useCallback, useMemo, useState } from 'react';
import { View, ActivityIndicator, Pressable as RNPressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import dayjs from '@/i18n/dayjs-config';
import { VStack } from '~/components/gluestack/vstack';
import { HStack } from '~/components/gluestack/hstack';
import { Text } from '~/components/gluestack/text';
import { Button, ButtonText } from '~/components/gluestack/button';
import { SkeletonText } from '@/components/gluestack/skeleton';
import { MemberDetails } from '~/models/MemberDetails';
import { WeightsService } from '@/services/domain/WeightsService';
import { WeightChartData, WeightRecord, WeightListView } from '@/models/Weight';
import { WeightChart } from '@/components/ui/WeightChart';
import { useMeasurementFormatters } from '~/components/providers/MeasurementsProvider';
import { ProfileSection } from './ProfileSection';
import { ProfileItem } from './ProfileItem';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { ContextSheet } from '@/components/ui/ContextSheet';
import WeightRecordSheet from './WeightRecordSheet';
import { useToastMessages } from '@/hooks/useToastMessages';
import { PageParam, getNextPageSkipOrCursor, buildPaginationParams } from '@/hooks/usePaginationUtils';
import {
  WeightScaleIcon,
  Target01Icon,
  ChartLineData02Icon,
  Add01Icon,
  Delete02Icon,
  DashboardSpeed02Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import {
  CircleArrowUpRight02Icon as CircleArrowUpRight02SolidIcon,
  CircleArrowDownRightIcon as CircleArrowDownRightSolidIcon,
} from '@hugeicons-pro/core-solid-rounded';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { useIsLargeScreen } from '~/hooks/useIsLargeScreen';

interface ProfileTabWeightProps {
  member: MemberDetails;
  isSelf?: boolean;
}

const PAGE_SIZE = 20;

export function ProfileTabWeight({ member, isSelf = false }: ProfileTabWeightProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isLargeScreen = useIsLargeScreen();
  const { formatWeightSimple, weightUnit } = useMeasurementFormatters();
  const { showSuccessToast, showErrorToast } = useToastMessages();

  const [isRecordSheetOpen, setIsRecordSheetOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<WeightRecord | null>(null);

  const weightQueryKey = useMemo(
    () => ['weights', 'list', member.Id],
    [member.Id]
  );
  const chartBuckets = isLargeScreen ? 24 : 16;
  const chartQueryKey = useMemo(
    () => ['weights', 'chart', member.Id, chartBuckets],
    [member.Id, chartBuckets]
  );

  // Chart data query
  const chartQuery = useQuery<WeightChartData>({
    queryKey: chartQueryKey,
    queryFn: async () => {
      const params = {
        ...(isSelf ? {} : { memberId: member.Id }),
        buckets: chartBuckets,
      };
      return await WeightsService.getChart(params);
    },
    retry: false,
  });

  // Weight list query (infinite)
  const listQuery = useInfiniteQuery({
    queryKey: weightQueryKey,
    queryFn: async ({ pageParam }) => {
      const baseParams = buildPaginationParams(pageParam, PAGE_SIZE);
      return await WeightsService.getWeights({
        ...baseParams,
        ...(isSelf ? {} : { memberId: member.Id }),
        sortBy: 'date',
        sortDir: 'desc',
      });
    },
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<WeightListView, PageParam>) => {
      const items = data.pages.flatMap((page) => page.Items);
      return { ...data, items };
    }, []),
  });

  const weightRecords = (listQuery.data as any)?.items as WeightRecord[] ?? [];
  const totalCount = listQuery.data?.pages?.[0]?.ItemCount;

  // Compute monthly rate from chart data
  const monthlyRate = useMemo(() => {
    if (!chartQuery.data?.Points) return null;
    const nonNull = chartQuery.data.Points.filter((p) => p.AvgWeightKg != null);
    if (nonNull.length < 2) return null;
    const first = nonNull[0];
    const last = nonNull[nonNull.length - 1];
    const monthsDiff = dayjs(last.StartDate).diff(dayjs(first.StartDate), 'month', true);
    if (monthsDiff < 0.5) return null;
    const kgDiff = last.AvgWeightKg! - first.AvgWeightKg!;
    return kgDiff / monthsDiff;
  }, [chartQuery.data]);

  // Calculate BMI
  const bmi = useMemo(() => {
    if (member.WeightKg == null || member.HeightCm == null || member.HeightCm === 0) return null;
    const heightM = member.HeightCm / 100;
    return member.WeightKg / (heightM * heightM);
  }, [member.WeightKg, member.HeightCm]);

  const invalidateQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: weightQueryKey }),
      queryClient.invalidateQueries({ queryKey: chartQueryKey }),
    ]);
  }, [queryClient, weightQueryKey, chartQueryKey]);

  const handleAddRecord = useCallback(() => {
    setEditingRecord(null);
    setIsRecordSheetOpen(true);
  }, []);

  const handleEditRecord = useCallback((record: WeightRecord) => {
    if (!isSelf) return;
    setEditingRecord(record);
    setIsRecordSheetOpen(true);
  }, [isSelf]);

  const handleDeleteRecord = useCallback(async (record: WeightRecord) => {
    try {
      await WeightsService.deleteWeight(record.Id);
      showSuccessToast(t('weightHistory.deleted', 'Weight record deleted'));
      await invalidateQueries();
    } catch (e) {
      showErrorToast(t('weightHistory.deleteFailed', 'Failed to delete record'), e as any);
    }
  }, [invalidateQueries, showSuccessToast, showErrorToast, t]);

  const handleSheetClose = useCallback(() => {
    setIsRecordSheetOpen(false);
    setEditingRecord(null);
  }, []);

  const handleRecordSaved = useCallback(async () => {
    handleSheetClose();
    await invalidateQueries();
  }, [handleSheetClose, invalidateQueries]);

  const isChartLoading = chartQuery.isPending;
  const isListLoading = listQuery.isPending;
  const hasChartData = (chartQuery.data?.Points?.length ?? 0) > 0;
  const hasListData = weightRecords.length > 0;

  return (
    <VStack className="gap-4 px-3 md:px-4">
      {/* Stats Section */}
      <ProfileSection title={t('weightHistory.stats', 'Stats')} noCard={!isLargeScreen}>
        <HStack className="gap-x-6 gap-y-3 flex-wrap md:gap-x-10 md:gap-y-4">
          {member.WeightKg != null && (
            <ProfileItem
              icon={WeightScaleIcon}
              label={t('weightHistory.currentWeight', 'Current')}
              labelPosition={isLargeScreen ? 'above' : 'hidden'}
              compact>
              {formatWeightSimple(member.WeightKg)}
            </ProfileItem>
          )}
          {bmi != null && (
            <ProfileItem
              icon={DashboardSpeed02Icon}
              label={t('weightHistory.bmi', 'BMI')}
              labelPosition={isLargeScreen ? 'above' : 'hidden'}
              compact>
              {!isLargeScreen && `${t('weightHistory.bmi', 'BMI')} `}
              {Math.round(bmi)}
            </ProfileItem>
          )}
          {monthlyRate != null && (
            <ProfileItem
              icon={ChartLineData02Icon}
              label={t('weightHistory.trend', 'Trend')}
              labelPosition={isLargeScreen ? 'above' : 'hidden'}
              compact>
              {monthlyRate > 0 ? '+' : ''}
              {formatWeightSimple(Math.abs(monthlyRate), 1)}
              {t('weightHistory.perMonth', '/mo')}
            </ProfileItem>
          )}
          {member.WeightGoalKg != null && (
            <ProfileItem
              icon={Target01Icon}
              label={t('weightHistory.goal', 'Goal')}
              labelPosition={isLargeScreen ? 'above' : 'hidden'}
              compact>
              {formatWeightSimple(member.WeightGoalKg)}
            </ProfileItem>
          )}
        </HStack>
      </ProfileSection>

      {/* Chart Section */}
      <ProfileSection>
        {isChartLoading ? (
          <VStack className="gap-3 py-6 items-center">
            <ActivityIndicator color="grey" />
          </VStack>
        ) : hasChartData ? (
          <WeightChart
            points={chartQuery.data!.Points}
            granularity={chartQuery.data!.Granularity}
            weightUnit={weightUnit}
            emptyMessage={t('weightHistory.noChartData', 'Not enough data for chart')}
          />
        ) : (
          <View className="items-center py-8">
            <Text size="sm" className="text-typography-500">
              {t('weightHistory.noChartData', 'Not enough data for chart')}
            </Text>
          </View>
        )}
      </ProfileSection>

      {/* Table Section */}
      <ProfileSection
        title={
          totalCount != null
            ? `${t('weightHistory.history', 'History')} (${totalCount})`
            : t('weightHistory.history', 'History')
        }>
        {/* Add button for own profile */}
        {isSelf && (
          <HStack className="justify-end mt-1 -mb-1">
            <ButtonWrapper
              icon={Add01Icon}
              text={t('weightHistory.addRecord', 'Add')}
              size="sm"
              onPress={handleAddRecord}
            />
          </HStack>
        )}

        {isListLoading ? (
          <VStack className="gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <HStack key={`skel-${i}`} className="gap-4 py-2">
                <SkeletonText className="h-4 w-24 rounded-md" />
                <SkeletonText className="h-4 w-16 rounded-md" />
                <SkeletonText className="h-4 flex-1 rounded-md" />
              </HStack>
            ))}
          </VStack>
        ) : listQuery.error ? (
          <VStack className="items-center py-6 gap-3">
            <Text size="sm" className="text-error-500">{t('common.error.general')}</Text>
            <Button
              variant="solid"
              action="primary"
              size="sm"
              onPress={() => listQuery.refetch()}>
              <ButtonText>{t('common.retry', 'Retry')}</ButtonText>
            </Button>
          </VStack>
        ) : !hasListData ? (
          <View className="items-center py-6">
            <Text size="sm" className="text-typography-500">
              {t('weightHistory.noData', 'No weight records yet')}
            </Text>
          </View>
        ) : (
          <VStack className="gap-0">
            {/* Table header */}
            <HStack className="gap-2 pb-2 border-b border-outline-100">
              <Text size="xs" bold className="w-28 uppercase text-typography-500">
                {t('weightHistory.date', 'Date')}
              </Text>
              <View className="w-4" />
              <Text size="xs" bold className="w-20 uppercase text-typography-500">
                {t('weightHistory.weight', 'Weight')}
              </Text>
              <Text size="xs" bold className="flex-1 uppercase text-typography-500">
                {t('weightHistory.notes', 'Notes')}
              </Text>
              {isSelf && <View className="w-8" />}
            </HStack>

            {/* Table rows */}
            {weightRecords.map((record, index) => {
              const prevRecord = weightRecords[index + 1];
              const diff = prevRecord ? record.WeightKg - prevRecord.WeightKg : null;
              const absDiff = diff != null ? Math.abs(diff) : 0;
              const showArrow = diff != null && absDiff >= 0.5;
              const isUp = diff != null && diff > 0;
              const wantsGain = member.WeightGoalKg != null && member.WeightGoalKg >= member.WeightKg!;
              const hasGoal = member.WeightGoalKg != null;
              const isPositive = hasGoal ? (wantsGain ? isUp : !isUp) : null;
              const TrendIcon = showArrow ? (isUp ? CircleArrowUpRight02SolidIcon : CircleArrowDownRightSolidIcon) : null;
              const trendColor = isPositive == null ? 'text-typography-400' : isPositive ? 'text-success-500' : 'text-error-500';

              return (
              <RNPressable
                key={record.Id}
                onPress={() => handleEditRecord(record)}
                disabled={!isSelf}
                style={({ pressed }) => ({ opacity: isSelf && pressed ? 0.6 : 1 })}>
                <HStack className="gap-2 py-3 border-b border-outline-50 items-start">
                  <Text size="sm" className="w-28 text-typography-800">
                    {dayjs.utc(record.DateWeighted).format('MMM D, YYYY')}
                  </Text>
                  <View className="w-4 items-center pt-0.5">
                    {TrendIcon && (
                      <HugeIconsWrapper
                        icon={TrendIcon}
                        size={14}
                        className={trendColor}
                      />
                    )}
                  </View>
                  <Text size="sm" className="w-20 font-semibold text-typography-900">
                    {formatWeightSimple(record.WeightKg)}
                  </Text>
                  <Text size="sm" className="flex-1 text-typography-600" numberOfLines={2}>
                    {record.Notes || ''}
                  </Text>
                  {isSelf && (
                    <RNPressable
                      onPress={(e) => {
                        e.stopPropagation?.();
                        handleDeleteRecord(record);
                      }}
                      hitSlop={8}
                      style={({ pressed }) => ({ opacity: pressed ? 0.5 : 0.6 })}>
                      <HugeIconsWrapper icon={Delete02Icon} size={16} className="text-error-400" />
                    </RNPressable>
                  )}
                </HStack>
              </RNPressable>
              );
            })}

            {/* Load more */}
            {listQuery.hasNextPage && (
              <View className="items-center py-4">
                {listQuery.isFetchingNextPage ? (
                  <ActivityIndicator color="grey" />
                ) : (
                  <Button
                    variant="outline"
                    action="neutral"
                    size="sm"
                    onPress={() => listQuery.fetchNextPage()}>
                    <ButtonText>{t('common.loadMore', 'Load more')}</ButtonText>
                  </Button>
                )}
              </View>
            )}
          </VStack>
        )}
      </ProfileSection>

      {/* Add/Edit Sheet */}
      <ContextSheet
        isOpen={isRecordSheetOpen}
        onClose={handleSheetClose}
        contentComponent={WeightRecordSheet}
        title={
          editingRecord
            ? t('weightHistory.editRecord', 'Edit weight')
            : t('weightHistory.addRecord', 'Add weight')
        }
        payload={{
          record: editingRecord,
          onSaved: handleRecordSaved,
          onDeleted: async () => {
            if (editingRecord) {
              await handleDeleteRecord(editingRecord);
              handleSheetClose();
            }
          },
          weightUnit,
        }}
      />
    </VStack>
  );
}
